/**
 * Build-time log level via webpack DefinePlugin: process.env.LOG_LEVEL
 * Defaults: development → debug, production → warn.
 * Override: LOG_LEVEL=error npm run build
 */

export type LogLevel = "silent" | "error" | "warn" | "info" | "debug";

const LEVELS: LogLevel[] = ["silent", "error", "warn", "info", "debug"];

const MESSAGE_RANK: Record<Exclude<LogLevel, "silent">, number> = {
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
};

const CONFIG_MAX: Record<LogLevel, number> = {
  silent: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
};

function resolveLevel(): LogLevel {
  const raw = (typeof process !== "undefined" && process.env && process.env.LOG_LEVEL) || "";
  const normalized = String(raw).toLowerCase() as LogLevel;
  if (LEVELS.includes(normalized) && normalized !== "silent") {
    return normalized;
  }
  if (normalized === "silent") {
    return "silent";
  }
  return process.env.NODE_ENV === "production" ? "warn" : "debug";
}

function enabled(method: keyof typeof MESSAGE_RANK): boolean {
  const config = resolveLevel();
  if (config === "silent") {
    return false;
  }
  return MESSAGE_RANK[method] <= CONFIG_MAX[config];
}

export const logger = {
  debug(...args: unknown[]): void {
    if (enabled("debug")) {
      console.debug(...args);
    }
  },

  info(...args: unknown[]): void {
    if (enabled("info")) {
      console.info(...args);
    }
  },

  warn(...args: unknown[]): void {
    if (enabled("warn")) {
      console.warn(...args);
    }
  },

  error(...args: unknown[]): void {
    if (enabled("error")) {
      console.error(...args);
    }
  },
};
